import React, {
  Fragment,
  useState,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Table } from 'reactstrap';
import { useIntl } from 'react-intl';

import { APIgetImports } from '../api/Importers';

import { userIsAdmin } from '../helpers/Auth';
import { getFormattedDateTime } from '../helpers/DateTime';
import { formatBytes } from '../helpers/Files';

const ImportList = ({
  importer,
  handleRetryImport,
  handleHideImport,
}) => {
  const intl = useIntl();
  const [imports, setImports] = useState([]);

  const loadImports = useCallback(() => (
    APIgetImports(importer.service).then((response) => {
      setImports(response);
    })
  ), [importer.service]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadImports();
    }, 3000);
    return () => clearInterval(interval);
  }, [loadImports]);

  if (imports.length === 0) {
    return null;
  }

  const removeFromImportList = (importUuid) => {
    setImports(imports.filter(i => (i.uuid !== importUuid)));
  };

  return (
    <div className="import-list content-wrapper">
      <h6>{intl.formatMessage({
        defaultMessage: 'Your {importer} imports',
      }, { importer: importer.name })}
      </h6>
      <Table className="import-list-table" responsive>
        <tbody>
          {imports.map(importItem => (
            <Fragment key={importItem.uuid}>
              {importItem.status === 'overlimit' && (
                <tr className={`import-list-item status-${importItem.status} message`}>
                  <td className="import-message" colSpan={4}>
                    {intl.formatMessage({
                      defaultMessage: `By importing the selected files and/or folders, you will reach your current storage limit.`,
                    })}{' '}
                    {userIsAdmin() ? (
                      <Fragment>
                        {intl.formatMessage({
                          defaultMessage: 'Please <a>upgrade your account here</a> or modify your selection to continue.',
                        }, {
                          a: (msg) => (
                            <a href="/admin/settings/subscription" target="_blank">
                              {msg}
                            </a>
                          ),
                        })}
                      </Fragment>
                    ) : (
                      <Fragment>
                        {intl.formatMessage({ defaultMessage: 'Please contact your administrator to upgrade the account.' })}
                      </Fragment>
                    )}
                  </td>
                </tr>
              )}
              <tr className={`import-list-item status-${importItem.status}${importItem.status === 'overlimit' ? ' with-message' : ''}`}>
                <th scope="row">
                  {intl.formatMessage({ defaultMessage: 'Started: {date}' }, { date: getFormattedDateTime(importItem.created_at) })}
                </th>
                <th scope="row" className="import-status">
                  {importItem.status === 'overlimit' ? 'failed' : importItem.status}
                </th>
                <td className="import-progress">
                  {intl.formatMessage({
                    defaultMessage: '{processed} of {total} files ({processedSize} of {totalSize})',
                  }, {
                    processed: importItem.files.files_processed_total,
                    total: importItem.files.files_total,
                    processedSize: formatBytes(importItem.files.files_processed_size_total),
                    totalSize: formatBytes(importItem.files.files_size_total),
                  })}
                </td>
                <td className="action-more">
                  {importItem.collection && (
                    <a href={`/collections/${importItem.collection.uuid}`} target="_blank" rel="noopener noreferrer">
                      {intl.formatMessage({ defaultMessage: 'Open collection' })}
                      <i className="fa-regular fa-layer-group" />
                    </a>
                  )}
                  {['overlimit', 'failed'].includes(importItem.status) && (
                    <a
                      href="#retry"
                      onClick={(e) => {
                        e.preventDefault();
                        removeFromImportList(importItem.uuid);
                        handleRetryImport(importItem.uuid).then(() => {
                          loadImports();
                        });
                      }}
                    >
                      {intl.formatMessage({ defaultMessage: 'Retry' })}
                      <i className="fa-regular fa-arrows-rotate" />
                    </a>
                  )}
                  {['finished', 'overlimit', 'failed'].includes(importItem.status) && (
                    <a
                      href="#hide"
                      onClick={(e) => {
                        e.preventDefault();
                        removeFromImportList(importItem.uuid);
                        handleHideImport(importItem.uuid).then(() => {
                          loadImports();
                        });
                      }}
                    >
                      {intl.formatMessage({ defaultMessage: 'Hide' })}
                      <i className="fa-regular fa-xmark" />
                    </a>
                  )}
                </td>
              </tr>
            </Fragment>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

ImportList.propTypes = {
  importer: PropTypes.object.isRequired,
  handleRetryImport: PropTypes.func.isRequired,
  handleHideImport: PropTypes.func.isRequired,
};

export default ImportList;
